import {useState, useCallback, useEffect, useRef} from 'react';

const STORAGE_PREFIX = 'ex::';
const DEBOUNCE_MS = 300;

export interface UsePersistedStateOptions {
  /**
   * When false, the hook behaves like plain useState — no reads from or
   * writes to localStorage. Useful when persistence is conditionally enabled
   * (e.g. only when a persistKey prop is provided). Default: true.
   */
  enabled?: boolean;
}

/**
 * A generic hook that behaves like `useState` but automatically persists
 * its value to localStorage under a namespaced key. Writes are debounced
 * to avoid thrashing during rapid updates (e.g. drag/resize).
 *
 * @param key - Unique identifier for this persisted slot (e.g. 'report-editor-dialog')
 * @param defaultValue - Initial state used when nothing is stored yet
 * @param options - Optional configuration (e.g. `{ enabled: false }` to disable persistence)
 * @returns [currentValue, setter, clear] — same shape as useState plus a clear function
 *
 * @example
 * const [layout, setLayout] = usePersistedState('my-dialog', { x: 0, y: 0 });
 * setLayout(prev => ({ ...prev, x: 100 }));
 *
 * @example
 * // Conditionally enabled — no localStorage access when disabled
 * const [cols, setCols] = usePersistedState('hidden-cols', [], { enabled: !!persistKey });
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T,
  options?: UsePersistedStateOptions
): [T, (valueOrUpdater: T | ((prev: T) => T)) => void, () => void] {

  const enabled = options?.enabled ?? true;

  // Initialize from localStorage or fall back to default
  const [value, setValueInternal] = useState<T>(() => {
    if (!enabled) {
      return defaultValue;
    }
    try {
      const stored = localStorage.getItem(STORAGE_PREFIX + key);
      return stored !== null ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  // Debounced write to localStorage
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistValue = useCallback(
    (newValue: T) => {
      if (!enabled) {
        return;
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(
        () => {
          try {
            localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(newValue));
          } catch { /* silently ignore quota/private mode errors */
          }
        },
        DEBOUNCE_MS
      );
    },
    [key, enabled]
  );

  // Public setter — supports direct value OR updater function
  const setValue = useCallback(
    (valueOrUpdater: T | ((prev: T) => T)) => {
      setValueInternal(prev => {
        const next = typeof valueOrUpdater === 'function'
          ? (valueOrUpdater as (prev: T) => T)(prev)
          : valueOrUpdater;
        persistValue(next);
        return next;
      });
    },
    [persistValue]
  );

  // Clear persisted state and reset to default
  const clearValue = useCallback(
    () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (enabled) {
        try {
          localStorage.removeItem(STORAGE_PREFIX + key);
        } catch {
          // Silently ignore errors
        }
      }
      setValueInternal(defaultValue);
    },
    [key, defaultValue, enabled]
  );

  // Flush pending timer on unmount
  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    []
  );

  return [value, setValue, clearValue];
}
